import type { Line } from '@nelnela/contracts';
import type { EmitInput } from '../lib/session';
import type { QueueLine } from '../lib/tts-queue';
import { wordTokens } from '../lib/text';

/**
 * The two decisions the Listen screen makes that are worth holding still (M6a step 12): which
 * lines a span contains, and what one line's playing writes to the event log.
 *
 * They live beside the screen rather than inside it because `Listen.tsx` is player wiring — an
 * audio element, a queue and three effects — and cannot be rendered under node, which is the only
 * UI test this repo has. `watch-behavior.ts` and `me-settings.ts` were carved out for the same
 * reason.
 */

export interface ListenSpan {
  startMs?: number;
  endMs?: number;
}

/**
 * A finite number off the query string — a span bound, and the bite's `planIdx` on the same rule.
 * Anything that is not a finite number is *absent*, never zero: a
 * `?startMs=` typo that read as 0 would silently widen a scene to the whole episode, and a NaN
 * compared with `>=` is false for every line, which would empty the screen with no explanation.
 */
export function spanBound(raw: string | null | undefined): number | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function parseSpan(params: { get(key: string): string | null }): ListenSpan {
  const startMs = spanBound(params.get('startMs'));
  const endMs = spanBound(params.get('endMs'));
  return {
    ...(startMs !== undefined ? { startMs } : {}),
    ...(endMs !== undefined ? { endMs } : {}),
  };
}

/**
 * The lines of a span, in play order.
 *
 * Half-open on the line's START, exactly as the server's vocabulary preview is
 * (`lib/video-decorations.ts` `loadSpanItems`: `l.start_ms >= $2 AND l.start_ms < $3`). That
 * parity is the point rather than a coincidence: the preview counts the words of a span and
 * offers "Listen to these", and a client that included a line by overlap would play a line whose
 * words the preview never counted — the screen would teach words the panel that launched it
 * said were not there. Omitting both bounds is the whole episode, the same as omitting them on
 * the preview.
 */
export function linesInSpan<T extends { startMs: number }>(
  lines: readonly T[],
  span: ListenSpan = {},
): T[] {
  return lines.filter(
    (l) =>
      (span.startMs === undefined || l.startMs >= span.startMs) &&
      (span.endMs === undefined || l.startMs < span.endMs),
  );
}

/** A transcript line as the queue wants it. The translation is dropped here, at the boundary —
 *  the screen never holds an English string it could accidentally render. */
export function toQueueLines(lines: readonly Line[]): QueueLine[] {
  return lines.map((l) => ({
    lineId: l.id,
    text: l.text,
    startMs: l.startMs,
    endMs: l.endMs,
  }));
}

export interface ListenContext {
  /** null when `activityStart` never landed — events still count, they just carry no activity */
  activityId: string | null;
  videoId: string;
}

/**
 * What one line's playing writes: an `exposure` per word, on the LISTEN axis, flagged as heard
 * without the crutch.
 *
 * `support.subtitlesHidden` is spelled the way the grader spells it (`lib/grader.ts` `lookupAxis`
 * reads it by name to move a lookup onto the listen axis). It changes no grade for the rows below
 * — an `exposure` already carries `modality: 'listen'`, so the axis is decided without it — and it
 * is recorded because it is TRUE, not to steer anything. It is honestly so: the Georgian line is
 * on screen small,
 * because a learner needs to know where in the scene they are, but there is no English anywhere
 * on it and no way to ask for any — the ear is the only route to meaning here, which is the whole
 * reason the screen exists.
 *
 * One row per word, and no `read` row beside it — the deliberate difference from Watch
 * (`Watch.tsx` emits both, since its transcript is always readable).
 *
 * `wordTokens` rather than `tokenize` + a skip: its offsets are the word's, which are the ones
 * the resolver stored in `line_item.char_start`, and punctuation is dropped by construction
 * rather than by a `continue` a later editor could quietly delete.
 */
export function exposuresForLine(line: QueueLine, ctx: ListenContext): EmitInput[] {
  return wordTokens(line.text).map((t) => ({
    activityId: ctx.activityId,
    activityType: 'listen' as const,
    eventType: 'exposure' as const,
    surface: t.word,
    modality: 'listen' as const,
    support: { subtitlesHidden: true },
    context: {
      videoId: ctx.videoId,
      lineId: line.lineId,
      charStart: t.charStart,
      charEnd: t.charEnd,
    },
  }));
}

/**
 * ⏮ — hearing a line again.
 *
 * This is the app's first LINE-SCOPED `replay` producer. M4's scene loop emits `replay` with a
 * `sceneId` and no line, and the fold's `expandLineScoped` has nothing to expand it over, so it
 * grades nothing; carrying the `lineId` here means the fold fans this one event out over every
 * item the line was tagged with, and asking to hear a line a second time finally counts as the
 * evidence it obviously is.
 */
export function replayForLine(line: QueueLine, ctx: ListenContext): EmitInput {
  return {
    activityId: ctx.activityId,
    activityType: 'listen',
    eventType: 'replay',
    modality: 'listen',
    support: { subtitlesHidden: true },
    context: { videoId: ctx.videoId, lineId: line.lineId },
  };
}

/** The player's per-track key for a line, so the screen can tell its own clip from anyone else's
 *  (a gloss sheet's "Hear it" shares the one player). */
export const listenKey = (lineId: string): string => `listen:${lineId}`;
